import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { getSpellDefinition } from '@shared/data/spells';

/**
 * Visuales de casteo sobre entidades: barra de progreso de cast de hechizo (showCastBar) y de
 * embarcar/desembarcar (showBoatBar), su actualización por frame (updateCastBars) y limpieza
 * (removeCastBar), más la animación en loop mientras se castea (playCastAnimation/removeCastAnimation).
 * Extraído de GameScene (god-object). El estado (activeCastBars, activeCastAnimations) vive en
 * GameScene y se accede vía `this.scene.*`. Ver docs/SPELLS.md, docs/WATER_AND_BOATS.md.
 */
export class CastVisualsController {
  constructor(private scene: GameScene) {}

  // CAST BAR (visible above player sprite, seen by everyone)
  // =========================================================================

  public showCastBar(entityId: string, spellId: string, castTimeSec: number): void {
    if (castTimeSec <= 0) return; // No cast bar for instant spells
    console.log(`[CastBar] showCastBar: entity=${entityId} spell=${spellId} duration=${castTimeSec}s hasPlayerObj=${this.scene.playerObjects.has(entityId)}`);
    this.removeCastBar(entityId); // clean previous

    const bar = this.scene.add.graphics().setDepth(610);
    const spell = getSpellDefinition(spellId);
    const label = spell?.nameEs ?? spellId;
    const text = this.scene.add.text(0, 0, `${label} ${castTimeSec.toFixed(1)}s`, {
      fontFamily: GAME_FONT_FAMILY,
      fontSize: '9px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 1,
      resolution: 2,
    }).setOrigin(0.5, 0.5).setDepth(611);

    this.scene.activeCastBars.set(entityId, {
      spellId,
      startTime: Date.now(),
      duration: castTimeSec * 1000,
      bar,
      text,
    });
  }

  /**
   * Barra de embarque/desembarque (canal de barco, tecla N). Reusa la infra de
   * la cast bar de hechizos pero en color náutico (celeste) y con label propio,
   * para distinguirla del casteo (naranja). Visible sobre el char para todos.
   * Ver docs/WATER_AND_BOATS.md.
   */
  public showBoatBar(entityId: string, kind: 'embark' | 'disembark', durationSec: number): void {
    if (durationSec <= 0) return;
    this.removeCastBar(entityId); // limpia cualquier barra previa (cast o barco)

    const bar = this.scene.add.graphics().setDepth(610);
    const label = kind === 'embark' ? 'Embarcando' : 'Desembarcando';
    const text = this.scene.add.text(0, 0, `${label} ${durationSec.toFixed(1)}s`, {
      fontFamily: GAME_FONT_FAMILY,
      fontSize: '9px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 1,
      resolution: 2,
    }).setOrigin(0.5, 0.5).setDepth(611);

    this.scene.activeCastBars.set(entityId, {
      spellId: `__boat_${kind}`,
      startTime: Date.now(),
      duration: durationSec * 1000,
      bar,
      text,
      fillColor: 0x2aa9e0, // celeste náutico (distinto del naranja de casteo)
      timerLabel: label,
    });
  }

  public removeCastBar(entityId: string): void {
    const cb = this.scene.activeCastBars.get(entityId);
    if (cb) {
      cb.bar.destroy();
      cb.text.destroy();
      this.scene.activeCastBars.delete(entityId);
    }
    // Also stop looping cast animation
    this.removeCastAnimation(entityId);
  }

  public updateCastBars(): void {
    const now = Date.now();
    const BAR_W = 50;
    const BAR_H = 7;

    for (const [entityId, cb] of this.scene.activeCastBars.entries()) {
      const elapsed = now - cb.startTime;
      const frac = Math.min(1, elapsed / cb.duration);

      if (frac >= 1) {
        this.removeCastBar(entityId);
        continue;
      }

      // Find player sprite position
      const playerObj = this.scene.playerObjects.get(entityId);
      if (!playerObj) {
        console.warn(`[CastBar] No playerObj for entityId=${entityId}, removing. Keys:`, Array.from(this.scene.playerObjects.keys()));
        this.removeCastBar(entityId);
        continue;
      }

      const x = playerObj.sprite.x;
      const y = playerObj.sprite.y - playerObj.sprite.displayHeight;

      const gfx = cb.bar;
      gfx.clear();

      // Black background
      gfx.fillStyle(0x000000, 0.8);
      gfx.fillRect(x - BAR_W / 2, y - BAR_H / 2, BAR_W, BAR_H);

      // Fill (naranja por defecto; celeste para el canal de barco)
      gfx.fillStyle(cb.fillColor ?? 0xff8800, 0.9);
      gfx.fillRect(x - BAR_W / 2 + 1, y - BAR_H / 2 + 1, (BAR_W - 2) * frac, BAR_H - 2);

      // Border
      gfx.lineStyle(1, 0x888888, 0.7);
      gfx.strokeRect(x - BAR_W / 2, y - BAR_H / 2, BAR_W, BAR_H);

      // Timer text (con label opcional — la barra de barco muestra "Embarcando 1.2s")
      const remaining = Math.max(0, (cb.duration - elapsed) / 1000);
      cb.text.setPosition(x, y - BAR_H / 2 - 7);
      cb.text.setText(cb.timerLabel ? `${cb.timerLabel} ${remaining.toFixed(1)}s` : `${remaining.toFixed(1)}s`);

      // Update looping cast animation position
      const castAnim = this.scene.activeCastAnimations.get(entityId);
      if (castAnim && playerObj) {
        const animDef = (this as any).animationsIndex?.[`animation${castAnim.texture.key.replace('animation', '')}`];
        const offsetY = animDef?.offsetY ?? 0;
        castAnim.setPosition(playerObj.sprite.x, playerObj.sprite.y + offsetY);
      }
    }
  }


  // =========================================================================
  // CAST ANIMATION (looping animation during cast, visible to all)
  // =========================================================================

  public playCastAnimation(entityId: string, animationId: number): void {
    this.removeCastAnimation(entityId);

    // Soporta tanto players como NPCs (los mobs castean spells también).
    const render =
      (this.scene.playerObjects.get(entityId) as { sprite: Phaser.GameObjects.Sprite } | undefined) ??
      (this.scene.npcObjects.get(entityId) as { sprite: Phaser.GameObjects.Sprite } | undefined);
    if (!render) return;

    const animKey = `animation${animationId}`;
    const animDef = (this as any).animationsIndex?.[animKey];
    const offsetY = animDef?.offsetY ?? 0;

    const spriteKey = `animation${animationId}`;
    const phaserAnimKey = `anim_${animationId}`;

    if (!this.scene.anims.exists(phaserAnimKey)) return;

    const sprite = this.scene.add.sprite(render.sprite.x, render.sprite.y + offsetY, spriteKey);
    sprite.setDepth(render.sprite.depth + 100);
    sprite.play({ key: phaserAnimKey, repeat: -1 }); // loop

    this.scene.activeCastAnimations.set(entityId, sprite);
  }

  public removeCastAnimation(entityId: string): void {
    const sprite = this.scene.activeCastAnimations.get(entityId);
    if (sprite) {
      sprite.destroy();
      this.scene.activeCastAnimations.delete(entityId);
    }
  }
}
